import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import Footer from '../components/Footer';
import FloatingThemeToggle from '../components/FloatingThemeToggle';

const PrivacyPage: React.FC = () => {
  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <FloatingThemeToggle />
      <div className="bg-gradient-to-br from-gray-900 light:from-gray-50 via-gray-800 light:via-gray-100 to-twitch-900 light:to-twitch-100 flex-1">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="mb-8">
            <Link
              to="/"
              className="inline-flex items-center text-gray-400 light:text-gray-600 hover:text-white light:hover:text-gray-900 transition-colors mb-4"
            >
              <ArrowLeftIcon className="h-4 w-4 mr-2" />
              Back to Home
            </Link>
            <h1 className="text-3xl font-bold text-white light:text-gray-900 mb-2">Privacy Policy</h1>
            <p className="text-gray-400 light:text-gray-600">Last updated: {new Date().toLocaleDateString()}</p>
          </div>

          <div className="bg-gray-800/50 light:bg-white/50 rounded-lg p-8 space-y-8">
            <section>
              <h2 className="text-xl font-semibold text-white light:text-gray-900 mb-4">Information We Collect</h2>
              <div className="text-gray-300 light:text-gray-700 space-y-4">
                <p>
                  Greetsy collects the following information when you use our service:
                </p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li><strong>Twitch Account Information:</strong> Your Twitch username, display name, and user ID when you authenticate with Twitch</li>
                  <li><strong>Chat Data:</strong> Messages and usernames from your Twitch chat for the purpose of greeting tracking</li>
                  <li><strong>Usage Data:</strong> Information about how you use Greetsy, including sessions created and greeting preferences</li>
                  <li><strong>Settings:</strong> Your personalized settings and preferences within the application</li>
                </ul>
              </div>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-white light:text-gray-900 mb-4">How We Use Your Information</h2>
              <div className="text-gray-300 light:text-gray-700 space-y-4">
                <p>We use the collected information to:</p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li>Provide and maintain the Greetsy service</li>
                  <li>Track unique chatters in your Twitch stream</li>
                  <li>Store your greeting preferences and session data</li>
                  <li>Improve and optimize our service</li>
                  <li>Communicate with you about service updates</li>
                </ul>
              </div>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-white light:text-gray-900 mb-4">Data Security</h2>
              <div className="text-gray-300 light:text-gray-700 space-y-4">
                <p>
                  We implement appropriate security measures to protect your personal information against unauthorized access, 
                  alteration, disclosure, or destruction. This includes:
                </p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li>Secure authentication via Twitch OAuth</li>
                  <li>Encrypted data transmission</li>
                  <li>Regular security audits and updates</li>
                  <li>Limited access to personal data on a need-to-know basis</li>
                </ul>
              </div>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-white light:text-gray-900 mb-4">Data Retention</h2>
              <div className="text-gray-300 light:text-gray-700 space-y-4">
                <p>
                  We retain your data for as long as your account is active or as needed to provide you services. 
                  You may delete your account at any time from the Settings page, which will permanently remove 
                  all associated data.
                </p>
              </div>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-white light:text-gray-900 mb-4">Third-Party Services</h2>
              <div className="text-gray-300 light:text-gray-700 space-y-4">
                <p>
                  Greetsy integrates with Twitch to provide its core functionality. We do not share your data 
                  with any other third parties except as required by law.
                </p>
              </div>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-white light:text-gray-900 mb-4">Your Rights</h2>
              <div className="text-gray-300 light:text-gray-700 space-y-4">
                <p>You have the right to:</p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li>Access your personal data</li>
                  <li>Correct inaccurate data</li>
                  <li>Delete your account and all associated data</li>
                  <li>Export your data</li>
                  <li>Withdraw consent at any time</li>
                </ul>
              </div>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-white light:text-gray-900 mb-4">Contact Us</h2>
              <div className="text-gray-300 light:text-gray-700">
                <p>
                  If you have any questions about this Privacy Policy, please contact us at:{' '}
                  <a href="mailto:hello@overcode.tv" className="text-twitch-400 hover:text-twitch-300">
                  hello@overcode.tv
                  </a>
                </p>
              </div>
            </section>
          </div>
        </div>
      </div>
      
      <Footer />
    </>
  );
};

export default PrivacyPage; 